<template>
  <UAlert
    :as="as"
    v-if="!isDismissed"
    role="alert"
    v-bind="$attrs"
    class="p-alert select-none"
    :class="props.class"
    :color="alertColor"
    :variant="variant"
    :orientation="orientation"
    :title="title"
    :description="description"
    :avatar="avatar"
    :close="Boolean(showClose || showTimeRemaining)"
  >
    <template v-if="!avatar" #leading="{ ui }">
      <FontAwesomeIcon :icon="alertIcon" aria-hidden="true" :class="ui.icon()" />
    </template>
    <template v-if="$slots.title" #title><slot name="title" /></template>
    <template v-if="$slots.description || $slots.default" #description>
      <slot name="description"><slot /></slot>
    </template>
    <template v-if="actions?.length || $slots.actions || $slots.action" #actions>
      <slot name="actions" :button-color="alertColor">
        <slot name="action" :button-color="alertColor">
          <PButton
            v-for="(action, index) in actions"
            :key="index"
            :color="alertColor"
            size="sm"
            v-bind="action"
          />
        </slot>
      </slot>
    </template>
    <template #close="{ ui }">
      <span v-if="showTimeRemaining" class="shrink-0 tabular-nums" :class="ui.description()">
        {{ formattedTime }}
      </span>
      <PButton
        v-if="showClose"
        variant="ghost"
        size="md"
        aria-label="Melding sluiten"
        class="size-5 shrink-0 p-0! hover:ring hover:ring-current hover:ring-inset focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
        :class="!closeOptions.color ? 'text-inherit! dark:text-inherit!' : undefined"
        :icon="closeIcon || ['fas', 'xmark']"
        v-bind="closeOptions"
        @click.stop="handleClose"
      />
    </template>
  </UAlert>
</template>

<script setup lang="ts">
import type { ComponentColor } from "@/config/component-colors"
import { computed, onMounted, onUnmounted, ref, type VNode } from "vue"
import { useStorage } from "@vueuse/core"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import UAlert, { type AlertProps } from "@nuxt/ui/components/Alert.vue"
import { AlertTypes } from "@/interfaces/component/alert.interface"
import PButton from "./PButton.vue"

type AlertColor = ComponentColor
type AlertButton = Pick<
  InstanceType<typeof PButton>["$props"],
  | "as"
  | "label"
  | "color"
  | "variant"
  | "size"
  | "block"
  | "icon"
  | "to"
  | "disabled"
  | "type"
  | "target"
  | "loading"
> &
  Pick<NonNullable<AlertProps["actions"]>[number], "onClick">

interface Props extends Omit<
  AlertProps,
  "ui" | "color" | "icon" | "close" | "closeIcon" | "actions"
> {
  color?: AlertColor
  icon?: [string, string]
  close?: boolean | Omit<AlertButton, "as" | "to" | "target" | "type">
  closeIcon?: [string, string]
  actions?: AlertButton[]
  /** The status of alert (success, warning, error, info) */
  type?: AlertTypes
  /** Whether the alert is closable */
  closable?: boolean
  /** Time in milliseconds before emitting close. */
  autoCloseMs?: number
  /** Whether to show the time remaining */
  showTimeRemaining?: boolean
  /** Persist manual dismissal under this storage key. */
  storageKey?: string
}

const props = withDefaults(defineProps<Props>(), {
  type: AlertTypes.NEUTRAL,
  as: "div",
  close: undefined,
  variant: "subtle",
  orientation: "vertical",
  showTimeRemaining: false
})

const emit = defineEmits<{ close: [] }>()
defineOptions({ inheritAttrs: false, name: "PAlert" })

defineSlots<{
  /** Description content when no named description slot is provided. */
  default?(): VNode[]
  title?(): VNode[]
  description?(): VNode[]
  actions?(props: { buttonColor: AlertColor }): VNode[]
  action?(props: { buttonColor: AlertColor }): VNode[]
}>()

const appearances = {
  [AlertTypes.NEUTRAL]: {
    icon: "circle-info",
    buttonColor: "info"
  },
  [AlertTypes.SUCCESS]: {
    icon: "circle-check",
    buttonColor: "success"
  },
  [AlertTypes.WARNING]: {
    icon: "triangle-exclamation",
    buttonColor: "warning"
  },
  [AlertTypes.ERROR]: {
    icon: "circle-xmark",
    buttonColor: "error"
  }
} as const

const appearance = computed(() => {
  if (props.color) {
    return (
      Object.values(appearances).find(({ buttonColor }) => buttonColor === props.color) ??
      appearances[props.type]
    )
  }

  return appearances[props.type]
})
const alertColor = computed(() => props.color ?? appearance.value.buttonColor)
const alertIcon = computed<IconProp>(() =>
  props.icon ? (props.icon as IconProp) : ["fas", appearance.value.icon]
)
const showClose = computed(() => props.close ?? props.closable)
const closeOptions = computed(() => (typeof props.close === "object" ? props.close : {}))

const dismissedStorage = props.storageKey ? useStorage(props.storageKey, false) : undefined
const isDismissed = computed(() => dismissedStorage?.value ?? false)
const timeLeft = ref(props.autoCloseMs ?? 0)
const formattedTime = computed(() => Math.ceil(timeLeft.value / 1000))
let closeTimeout: ReturnType<typeof setTimeout> | undefined
let countdownInterval: ReturnType<typeof setInterval> | undefined

function clearTimers() {
  clearTimeout(closeTimeout)
  clearInterval(countdownInterval)
}

function handleClose() {
  clearTimers()
  if (dismissedStorage) dismissedStorage.value = true
  emit("close")
}

onMounted(() => {
  if (isDismissed.value || props.autoCloseMs === undefined) return

  countdownInterval = setInterval(() => {
    timeLeft.value = Math.max(0, timeLeft.value - 1000)
    if (timeLeft.value === 0) clearInterval(countdownInterval)
  }, 1000)

  closeTimeout = setTimeout(() => {
    clearTimers()
    timeLeft.value = 0
    emit("close")
  }, props.autoCloseMs)
})

onUnmounted(clearTimers)
</script>
